/**
 * CAPACIDAD Y CARGA DE UN CENTRO — FASE 6.4 §Q/§R/§S
 *
 * Resumen de planificación: capacidad estimada, carga planificada,
 * conflictos y alertas — NUNCA el planner visual (eso es una fase
 * posterior). Todo sale de `GET /api/centros/:id/planificacion/resumen`,
 * un único fetch por rango de fechas; los filtros de la bandeja de
 * alertas son en cliente, sobre lo ya cargado.
 *
 * NOTA DE NOMBRE: la pestaña "Planificación" del centro ya existe (planes
 * de limpieza, `paginas/planificacion.tsx`) — para no chocar con ese
 * nombre ya establecido, esta sección se llama «Capacidad y carga».
 *
 * ASIGNADO ≠ EJECUTOR REAL, y nada de esto bloquea nada: son señales
 * informativas. Vocabulario obligatorio: «disponibilidad de
 * planificación», «carga estimada», «capacidad estimada», «cobertura»,
 * «conflicto de planificación» — nunca «jornada laboral», «rendimiento»
 * ni cualquier juicio de desempeño individual.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  api,
  type AlertaPlanificacion,
  type CargaOperarioResumen,
  type CoberturaPlan,
  type EtiquetaCarga,
  type MetricasDia,
  type ResumenPlanificacion,
  type SeveridadAlertaPlanificacion,
  type TipoAlertaPlanificacion,
} from '../api.ts';
import { hoyLocal, Panel, useDatos, Vacio } from './base.tsx';

/** Fecha de calendario LOCAL (antes UTC: de madrugada, el resumen empezaba ayer). */
function hoyIso(): string {
  return hoyLocal();
}
function masDias(iso: string, dias: number): string {
  return new Date(Date.parse(iso) + dias * 86_400_000).toISOString().slice(0, 10);
}
function horas(minutos: number): string {
  const h = minutos / 60;
  return `${Number.isInteger(h) ? String(h) : h.toFixed(1)}h`;
}
/** FINAL GATE §1: capacidad desconocida nunca se muestra como "0h" — nunca se finge un dato que no hay. */
function horasOSinConfigurar(minutos: number | null): string {
  return minutos === null ? 'Sin configurar' : horas(minutos);
}
const FORMATO_DIA = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
function etiquetaDia(iso: string): string {
  return FORMATO_DIA.format(new Date(`${iso}T00:00:00Z`));
}

export function PlanificacionResumen({ idCentro }: { idCentro: string }): ReactNode {
  const [desde, setDesde] = useState(hoyIso);
  const [hasta, setHasta] = useState(() => masDias(hoyIso(), 6));
  const resumen = useDatos(
    () => api.get<ResumenPlanificacion>(`/api/centros/${idCentro}/planificacion/resumen?desde=${desde}&hasta=${hasta}`),
    [idCentro, desde, hasta],
  );

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Capacidad y carga</h2>
        <div className="fila">
          <label className="fila" style={{ gap: 6, fontWeight: 'normal' }}>
            Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label className="fila" style={{ gap: 6, fontWeight: 'normal' }}>
            Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
        </div>
      </div>
      <div className="panel-cuerpo">
        <Panel estado={resumen}>
          {(r) => (
            <>
              <ResumenTotales totales={r.totales} />
              {r.cobertura.length > 0 && <SeccionCobertura cobertura={r.cobertura} />}
              <SeccionTitulo texto="Por día" />
              <ListaDias dias={r.dias} />
              <SeccionTitulo texto="Carga por operario" />
              <TablaOperarios operarios={r.operarios} />
              <SeccionTitulo texto="Alertas" />
              <BandejaAlertas alertas={r.alertas} operarios={r.operarios} />
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

function SeccionTitulo({ texto }: { texto: string }): ReactNode {
  return <h3 style={{ margin: '20px 0 10px' }}>{texto}</h3>;
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{etiqueta}</div>
      <div className="valor-dato">{valor}</div>
    </div>
  );
}

function ResumenTotales({ totales }: { totales: ResumenPlanificacion['totales'] }): ReactNode {
  return (
    <div className="datos">
      <Dato etiqueta="Horas estimadas" valor={horas(totales.minutosCarga)} />
      <Dato etiqueta="Capacidad estimada" valor={horas(totales.minutosCapacidad)} />
      <Dato etiqueta="OT planificadas" valor={String(totales.numeroOts)} />
      <Dato etiqueta="OT sin asignar" valor={String(totales.otsSinAsignar)} />
      <Dato etiqueta="Operarios asignados" valor={String(totales.operariosAsignados)} />
      <Dato etiqueta="Alertas" valor={String(totales.numeroAlertas)} />
    </div>
  );
}

function SeccionCobertura({ cobertura }: { cobertura: readonly CoberturaPlan[] }): ReactNode {
  return (
    <div style={{ marginTop: 16 }}>
      <SeccionTitulo texto="Cobertura de planificación" />
      <div className="tabla-desliza">
        <table>
          <thead>
            <tr>
              <th>Plan</th>
              <th>Materializado hasta</th>
              <th>Días de cobertura</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cobertura.map((c) => (
              <tr key={c.idPlan}>
                <td className="mono">{c.codigoPlan}</td>
                <td>{c.fechaMaximaMaterializada ?? 'Sin OT materializada'}</td>
                <td>{c.diasCobertura ?? '—'}</td>
                <td>{c.baja && <span className="etiqueta info">Cobertura baja</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ListaDias({ dias }: { dias: readonly MetricasDia[] }): ReactNode {
  if (dias.length === 0) return <Vacio titulo="Sin días en el rango elegido" />;
  return (
    <div className="tabla-desliza">
      <table>
        <thead>
          <tr>
            <th>Día</th>
            <th>OT</th>
            <th>Carga</th>
            <th>Capacidad</th>
            <th>Sin asignar</th>
            <th>Alertas</th>
          </tr>
        </thead>
        <tbody>
          {dias.map((d) => (
            <tr key={d.fecha}>
              <td>{etiquetaDia(d.fecha)}</td>
              <td>{d.numeroOts}</td>
              <td>{horas(d.minutosCarga)}</td>
              <td>
                {horas(d.minutosCapacidad)}
                {d.operariosSinConfigurar > 0 && (
                  <span className="pequena tenue" title="Operarios sin disponibilidad configurada, no incluidos en esta cifra">
                    {' '}(+{d.operariosSinConfigurar} sin configurar)
                  </span>
                )}
              </td>
              <td>{d.otsSinAsignar > 0 ? <span className="etiqueta aviso">{d.otsSinAsignar}</span> : 0}</td>
              <td>{d.numeroAlertas > 0 ? <span className="etiqueta aviso">{d.numeroAlertas}</span> : 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function claseEtiquetaCarga(etiqueta: EtiquetaCarga): string {
  if (etiqueta === 'SOBRECARGA_ESTIMADA') return 'aviso';
  if (etiqueta === 'CAPACIDAD_COMPLETA') return 'info';
  if (etiqueta === 'SIN_CONFIGURAR') return 'neutra';
  return 'neutra';
}
function textoEtiquetaCarga(etiqueta: EtiquetaCarga): string {
  switch (etiqueta) {
    case 'SIN_CARGA': return 'Sin carga';
    case 'CARGA_PARCIAL': return 'Carga parcial';
    case 'CAPACIDAD_COMPLETA': return 'Capacidad completa';
    case 'SOBRECARGA_ESTIMADA': return 'Sobrecarga estimada';
    case 'SIN_CONFIGURAR': return 'Sin disponibilidad configurada';
  }
}

function TablaOperarios({ operarios }: { operarios: readonly CargaOperarioResumen[] }): ReactNode {
  if (operarios.length === 0) return <Vacio titulo="Sin operarios habilitados en este centro" />;
  return (
    <div className="tabla-desliza">
      <table>
        <thead>
          <tr>
            <th>Operario</th>
            <th>Disponibilidad</th>
            <th>Carga</th>
            <th>%</th>
            <th>OT</th>
            <th>Estado</th>
            <th>Alertas</th>
          </tr>
        </thead>
        <tbody>
          {operarios.map((o) => (
            <tr key={o.idOperario}>
              <td>
                <Link to={`/operarios/${o.idOperario}`}>{o.operarioNombre}</Link>
              </td>
              <td>{horasOSinConfigurar(o.minutosCapacidad)}</td>
              <td>{horas(o.minutosCarga)}</td>
              <td>{o.porcentajeCarga === null ? '—' : `${String(o.porcentajeCarga)}%`}</td>
              <td>{o.numeroOts}</td>
              <td>
                <span className={`etiqueta ${claseEtiquetaCarga(o.etiqueta)}`} title={o.etiqueta === 'SIN_CONFIGURAR' ? 'Este operario no tiene disponibilidad de planificación configurada.' : undefined}>
                  {textoEtiquetaCarga(o.etiqueta)}
                </span>
              </td>
              <td>{o.numeroAlertas > 0 ? <span className="etiqueta aviso">{o.numeroAlertas}</span> : 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ETIQUETA_TIPO_ALERTA: Record<TipoAlertaPlanificacion, string> = {
  OT_SIN_ASIGNAR: 'OT sin asignar',
  OPERARIO_NO_APTO: 'Operario no apto',
  SOLAPAMIENTO: 'Solapamiento',
  FUERA_DISPONIBILIDAD: 'Fuera de disponibilidad',
  SOBRECARGA: 'Sobrecarga',
  BLOQUEO: 'Bloqueo',
  OT_VENCIDA: 'OT vencida',
  COBERTURA_BAJA: 'Cobertura baja',
  DISPONIBILIDAD_SIN_CONFIGURAR: 'Disponibilidad sin configurar',
};
function claseSeveridad(s: SeveridadAlertaPlanificacion): string {
  if (s === 'ALTA') return 'error';
  if (s === 'AVISO') return 'aviso';
  return 'info';
}

function BandejaAlertas({ alertas, operarios }: { alertas: readonly AlertaPlanificacion[]; operarios: readonly CargaOperarioResumen[] }): ReactNode {
  const [tipo, setTipo] = useState('');
  const [severidad, setSeveridad] = useState('');
  const [fecha, setFecha] = useState('');
  const [idOperario, setIdOperario] = useState('');

  const filtradas = alertas.filter(
    (a) =>
      (tipo === '' || a.tipo === tipo) &&
      (severidad === '' || a.severidad === severidad) &&
      (fecha === '' || a.fecha === fecha) &&
      (idOperario === '' || a.idOperario === idOperario),
  );

  if (alertas.length === 0) return <Vacio titulo="Sin alertas en este período" />;

  return (
    <div>
      <div className="filtros">
        <div className="campo">
          <label htmlFor="alerta-tipo">Tipo</label>
          <select id="alerta-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(ETIQUETA_TIPO_ALERTA).map(([v, t]) => (
              <option key={v} value={v}>{t}</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="alerta-severidad">Severidad</label>
          <select id="alerta-severidad" value={severidad} onChange={(e) => setSeveridad(e.target.value)}>
            <option value="">Todas</option>
            <option value="INFO">Info</option>
            <option value="AVISO">Aviso</option>
            <option value="ALTA">Alta</option>
          </select>
        </div>
        <div className="campo">
          <label htmlFor="alerta-fecha">Fecha</label>
          <input id="alerta-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </div>
        {operarios.length > 0 && (
          <div className="campo">
            <label htmlFor="alerta-operario">Operario</label>
            <select id="alerta-operario" value={idOperario} onChange={(e) => setIdOperario(e.target.value)}>
              <option value="">Todos</option>
              {operarios.map((o) => (
                <option key={o.idOperario} value={o.idOperario}>{o.operarioNombre}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {filtradas.length === 0 ? (
        <Vacio titulo="Ninguna alerta cumple los filtros" />
      ) : (
        <div className="tabla-desliza">
          <table>
            <thead>
              <tr>
                <th>Severidad</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Mensaje</th>
                <th>Referencia</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((a, i) => (
                <tr key={i}>
                  <td>
                    <span className={`etiqueta ${claseSeveridad(a.severidad)}`}>{a.severidad}</span>
                  </td>
                  <td>{ETIQUETA_TIPO_ALERTA[a.tipo]}</td>
                  <td className="mono">{a.fecha ?? '—'}</td>
                  <td>
                    {a.mensaje}
                    {a.detalles !== undefined && <div className="pequena tenue">{a.detalles}</div>}
                  </td>
                  <td>
                    {a.idOt !== undefined && (
                      <Link to={`/ots/${a.idOt}`}>{a.codigoOt ?? 'Ver OT'}</Link>
                    )}
                    {a.idOt !== undefined && a.idOperario !== undefined && ' · '}
                    {a.idOperario !== undefined && (
                      <Link to={`/operarios/${a.idOperario}`}>{a.operarioNombre ?? 'Ver operario'}</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
